import cx from 'classnames';
import { MouseEvent, MouseEventHandler } from 'react';
import { useIntl } from 'react-intl';

import { PaginationState } from '../../../core/Pagination/Pagination';
import { ampli } from '../../../core/Tracking/Amplitude/__AUTO_GENERATED__';
import {
    TrackingProperties,
    useTrackEventAmplitude,
} from '../../../core/Tracking/Amplitude/Amplitude';
import { IconChevronLeft } from '../icons/ChevronLeft';
import { IconChevronRight } from '../icons/ChevronRight';
import { Typography } from '../typography/Typography';

import style from './PaginationNavigation.module.css';

export function PaginationNavigation({
    pagination,
    generateUrl,
    onPageChange,
    trackingInformation,
}: {
    pagination: PaginationState<unknown>;
    generateUrl: (page: number) => string;
    onPageChange: (page: number) => void;
    trackingInformation: {
        click: Pick<TrackingProperties<typeof ampli.paginationClicked>, 'block'>;
    };
}) {
    const { trackEvent } = useTrackEventAmplitude();

    const pagesNumbers = Array.from({ length: pagination.lastPage }, (_, index) => {
        return index + 1;
    });

    function handlePageClick(page: number) {
        if (page === pagination.currentPage) {
            return;
        }

        onPageChange(page);

        trackEvent((ampli, defaultProperties) => {
            ampli.paginationClicked({
                ...defaultProperties,
                ...trackingInformation.click,
                click_type: 'number',
                page_number: page,
            });
        });
    }

    function handlePreviousClick() {
        const { previousPage } = pagination;

        if (previousPage === null) {
            return;
        }

        onPageChange(previousPage);

        trackEvent((ampli, defaultProperties) => {
            ampli.paginationClicked({
                ...defaultProperties,
                ...trackingInformation.click,
                click_type: 'previous',
                page_number: previousPage,
            });
        });
    }

    function handleNextClick() {
        const { nextPage } = pagination;

        if (nextPage === null) {
            return;
        }

        onPageChange(nextPage);

        trackEvent((ampli, defaultProperties) => {
            ampli.paginationClicked({
                ...defaultProperties,
                ...trackingInformation.click,
                click_type: 'next',
                page_number: nextPage,
            });
        });
    }

    return (
        <div className={style.container}>
            <PaginationNavigationNavButton
                direction="previous"
                pagination={pagination}
                onClick={handlePreviousClick}
            />

            {pagesNumbers.map((page) => {
                return (
                    <PaginationNavigationPageButton
                        key={page}
                        page={page}
                        generateUrl={generateUrl}
                        isActive={page === pagination.currentPage}
                        onClick={handlePageClick}
                    />
                );
            })}

            <PaginationNavigationNavButton
                direction="next"
                pagination={pagination}
                onClick={handleNextClick}
            />
        </div>
    );
}

function PaginationNavigationNavButton({
    pagination,
    direction,
    onClick,
}: {
    pagination: PaginationState<unknown>;
    direction: 'previous' | 'next';
    onClick: MouseEventHandler<HTMLButtonElement>;
}) {
    const { firstPage, lastPage, currentPage } = pagination;
    const { formatMessage } = useIntl();
    const isActive =
        direction === 'previous' ? firstPage === currentPage : lastPage === currentPage;
    const Icon = direction === 'previous' ? IconChevronLeft : IconChevronRight;
    const ariaLabel = formatMessage({
        id:
            direction === 'previous'
                ? 'global.component.carousel.nav.previous'
                : 'global.component.carousel.nav.next',
    });

    return (
        <button
            type="button"
            aria-label={ariaLabel}
            disabled={isActive}
            className={cx(style.navButton, {
                [style.active]: isActive,
            })}
            onClick={onClick}
        >
            <Icon className={style.navButtonIcon} />
        </button>
    );
}

function PaginationNavigationPageButton({
    page,
    generateUrl,
    isActive,
    onClick,
}: {
    page: number;
    generateUrl: (page: number) => string;
    isActive: boolean;
    onClick: (page: number) => void;
}) {
    const props = isActive
        ? { as: 'span' as const }
        : {
              as: 'a' as const,
              href: generateUrl(page),
              onClick: (event: MouseEvent<HTMLAnchorElement>) => {
                  event.preventDefault();
                  onClick(page);
              },
          };

    return (
        <Typography
            {...props}
            scale="primary-body-bold"
            className={cx(style.pageButton, {
                [style.active]: isActive,
            })}
        >
            {page}
        </Typography>
    );
}
